import { useEffect, useState } from "react";
import BrandShell from "../components/BrandShell";
import SEO from "../components/SEO";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Megaphone, Send, Users } from "lucide-react";

const AUDIENCES = [
  ["incomplete_creators", "Creators with incomplete profiles"], ["creators", "All creators"], ["brands", "All brands"],
  ["unverified_brands", "Brands not yet verified"], ["all", "Everyone"],
];
const TEMPLATES = {
  complete_profile: {
    audience: "incomplete_creators", category: "reminders",
    subject: "Complete your Nexus profile — new brand campaigns need creators this week",
    title: "Complete your profile to get picked for this week's campaigns",
    body: "Brands shortlist creators straight from their profiles — and several new brand campaigns have just landed on Nexus looking for creators right now.\n\nA complete profile (photo, tagline, bio, niches, 2+ portfolio videos and a rate card) gets roughly 4× more offers and ranks higher in Discover.\n\nIt takes about five minutes. Finish yours today so you're in the running.",
    cta_label: "Complete my profile →", cta_url: "/creator/profile",
  },
  new_briefs: {
    audience: "creators", category: "news", subject: "New briefs just dropped on Nexus", title: "New brand briefs are live",
    body: "Fresh campaigns were posted today. Apply early — brands usually shortlist within 48 hours.", cta_label: "Browse briefs →", cta_url: "/campaigns",
  },
  verify_brand: {
    audience: "unverified_brands", category: "reminders", subject: "One step left: verify your business on Nexus", title: "Verify your business to make your briefs visible",
    body: "Creators only see briefs from verified brands. Upload a director's ID or your Companies House certificate — it takes a minute and we approve within 24 hours.", cta_label: "Verify now →", cta_url: "/settings#identity",
  },
};

export default function AdminBroadcast() {
  const [form, setForm] = useState({ ...TEMPLATES.complete_profile, send_email: true, send_push: true });
  const [count, setCount] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const loadHistory = () => api.get("/admin/broadcasts").then((r) => setHistory(r.data.rows)).catch(() => {});
  useEffect(() => { loadHistory(); }, []);
  useEffect(() => { setCount(null); api.get("/admin/broadcast/audience", { params: { audience: form.audience } }).then((r) => setCount(r.data.count)).catch(() => setCount(0)); }, [form.audience]);

  const send = async () => {
    if (!window.confirm(`Send to ${count} ${AUDIENCES.find((a) => a[0] === form.audience)?.[1]}? This emails real users.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/broadcast", form);
      toast.success(`Sending to ${data.recipients} users — check history for progress`);
      setTimeout(loadHistory, 1500); setTimeout(loadHistory, 8000);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Send failed"); }
    finally { setBusy(false); }
  };

  const cls = "bg-white/[0.02] border-white/10 text-white";
  return (
    <BrandShell>
      <SEO title="Broadcast · Admin" description="Message creators and brands." path="/admin/broadcast" />
      <div className="max-w-5xl mx-auto px-4 md:px-6 lg:px-10 py-8 space-y-6" data-testid="admin-broadcast-page">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2 flex items-center gap-2"><Megaphone className="h-3.5 w-3.5 text-nexus-pink" /> Admin</div>
          <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tighter">Message your users</h1>
          <p className="text-zinc-400 mt-2 text-sm">Goes to each user's in-app inbox, plus email and push if enabled. Users who opted out of that email category won't get the email (they still get the inbox message).</p>
        </div>
        <div className="flex gap-2 flex-wrap" data-testid="broadcast-templates">
          {Object.entries(TEMPLATES).map(([k, t]) => (
            <button key={k} onClick={() => setForm((f) => ({ ...f, ...t }))} className="px-3 h-8 rounded-full text-xs border border-white/10 text-zinc-300 hover:border-nexus-purple hover:text-white" data-testid={`broadcast-template-${k}`}>{t.title.slice(0, 40)}{t.title.length > 40 ? "…" : ""}</button>
          ))}
        </div>
        <div className="glass rounded-2xl p-6 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Audience</div>
              <select value={form.audience} onChange={(e) => set("audience", e.target.value)} className="h-10 w-full rounded-md bg-[#0C0C10] border border-white/10 text-white text-sm px-2" data-testid="broadcast-audience">
                {AUDIENCES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              <div className="text-xs text-zinc-400 mt-1.5 inline-flex items-center gap-1.5" data-testid="broadcast-audience-count"><Users className="h-3.5 w-3.5" /> {count === null ? "Counting…" : `${count} recipient${count === 1 ? "" : "s"}`}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Email category (respects unsubscribe)</div>
              <select value={form.category} onChange={(e) => set("category", e.target.value)} className="h-10 w-full rounded-md bg-[#0C0C10] border border-white/10 text-white text-sm px-2" data-testid="broadcast-category">
                <option value="news">Product news</option><option value="reminders">Reminders</option><option value="deals">Deal updates</option>
              </select>
            </div>
          </div>
          <div><div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Email subject</div><Input value={form.subject} onChange={(e) => set("subject", e.target.value)} className={cls} data-testid="broadcast-subject" /></div>
          <div><div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Headline (in-app + email title)</div><Input value={form.title} onChange={(e) => set("title", e.target.value)} className={cls} data-testid="broadcast-title" /></div>
          <div><div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Message</div><Textarea rows={7} value={form.body} onChange={(e) => set("body", e.target.value)} className={cls} data-testid="broadcast-body" /></div>
          <div className="grid md:grid-cols-2 gap-4">
            <div><div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Button label</div><Input value={form.cta_label} onChange={(e) => set("cta_label", e.target.value)} className={cls} data-testid="broadcast-cta-label" /></div>
            <div><div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Button link (path or URL)</div><Input value={form.cta_url} onChange={(e) => set("cta_url", e.target.value)} className={cls} data-testid="broadcast-cta-url" /></div>
          </div>
          <div className="flex items-center gap-6 text-sm text-zinc-300">
            <label className="flex items-center gap-2">Email <Switch checked={form.send_email} onCheckedChange={(v) => set("send_email", v)} data-testid="broadcast-email-switch" /></label>
            <label className="flex items-center gap-2">Push <Switch checked={form.send_push} onCheckedChange={(v) => set("send_push", v)} data-testid="broadcast-push-switch" /></label>
            <span className="text-xs text-zinc-500">In-app inbox: always</span>
          </div>
          <Button onClick={send} disabled={busy || !count} className="rounded-full nexus-gradient text-white border-0 font-semibold" data-testid="broadcast-send-btn"><Send className="h-4 w-4 mr-2" /> Send to {count ?? "…"} users</Button>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3">History</div>
          {history.length === 0 ? <div className="text-sm text-zinc-500" data-testid="broadcast-history-empty">No broadcasts yet.</div> : (
            <div className="space-y-2" data-testid="broadcast-history">
              {history.map((h) => (
                <div key={h.id} className="glass rounded-xl p-3 flex items-center justify-between gap-3 text-sm" data-testid={`broadcast-row-${h.id}`}>
                  <div className="min-w-0"><div className="text-white truncate">{h.title}</div><div className="text-xs text-zinc-500">{AUDIENCES.find((a) => a[0] === h.audience)?.[1]} · {new Date(h.created_at).toLocaleString("en-GB")}</div></div>
                  <div className={`text-xs shrink-0 ${h.status === "sent" ? "text-emerald-300" : "text-amber-300"}`}>{h.status === "sent" ? `Sent to ${h.sent}/${h.recipients}` : `Sending… ${h.recipients}`}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </BrandShell>
  );
}
